/**
 * "Moderate Chat only" — a View-level team moderator restricted to the livestream
 * Chat tab's Live room.
 *
 * Six surfaces on the livestream manage page need identical treatment (tab bar,
 * stream title, Enable/Disable, Delete, the ingest sidebar, the Audience sub-tabs
 * and master switch), which is exactly the situation where the treatment drifts.
 * They all read `locked` and `tooltip` from here.
 *
 * This is presentation only. The real boundary is `enforceChatOnlyModeration` in
 * the castr API (`api/teams.js`) — reverting this file leaves enforcement intact.
 */
import { computed, unref } from 'vue'
import { useStore } from 'vuex'

export const CHAT_ONLY_TOOLTIP = 'You do not have permission to access this.'

/** Routes a chat-only moderator may reach on a livestream. */
export const CHAT_ONLY_ROUTES = [
	'LivestreamManageAudience',
	'LivestreamManageAudienceLiveRoom',
]

/**
 * Is this permission document a chat-only moderator?
 *
 * Both halves are required, matching `isChatModerationOnlyAllowed` in the castr
 * API: the flag narrows `liveStreamView`, so it means nothing without it. Admins
 * never carry it (`createAdminPermissions` forces it false), and the role is
 * tested for `admin` rather than `moderator` because older permission documents
 * can have an undefined role that a `role === 'moderator'` test would let through.
 *
 * Takes a plain permissions object so the card, the router and the manage page
 * all share one definition.
 */
export function isChatOnlyModerator(permissions) {
	return Boolean(
		permissions
		&& permissions.role !== 'admin'
		&& permissions.liveStreamView
		&& permissions.liveStreamChatModerationOnly,
	)
}

/**
 * A team moderator may open a livestream's manage page only with Edit-or-higher
 * (`liveStreamManage`), or as a chat-only moderator, for whom the Chat tab is the
 * entire point of the flag. View on its own grants the stream LIST and nothing
 * more, so such a member is turned away from the manage page.
 *
 * Returns false when permissions have not hydrated yet: a cold load must not
 * bounce an owner off their own stream. The API is the real boundary.
 */
export function isLivestreamManageBlocked(storeInstance) {
	if (!storeInstance) return false
	const activeTeam = storeInstance.getters['User/activeTeam']
	const permissions = storeInstance.getters['User/userPermissions']
	if (!activeTeam || !permissions) return false
	if (permissions.role === 'admin') return false
	if (permissions.liveStreamManage) return false
	return !isChatOnlyModerator(permissions)
}

/**
 * Store-argument form, so the router (which has no component instance and cannot
 * call `useStore`) and the components share one implementation. `activeTeam` is
 * what keeps a personal session unaffected.
 */
export function isChatOnlyLocked(storeInstance) {
	if (!storeInstance) return false
	const activeTeam = storeInstance.getters['User/activeTeam']
	return Boolean(activeTeam && isChatOnlyModerator(storeInstance.getters['User/userPermissions']))
}

/** True when `routeName` is off-limits for a chat-only moderator. */
export function isChatOnlyBlockedRoute(routeName) {
	return !!routeName && !CHAT_ONLY_ROUTES.includes(routeName)
}

/**
 * @param appliesTo Ref/getter guarding whether the current view is in scope. The
 *   manage page is shared by Multistream, Pre-recorded and VOD, and the flag is
 *   livestream-only, so those callers pass `stream.type === 'live'`. It is also
 *   what keeps the lock off while the stream is still being fetched.
 */
export function useChatOnlyLock(appliesTo = true) {
	const store = useStore()
	const locked = computed(() => isChatOnlyLocked(store) && !!unref(appliesTo))

	/** True when this surface should render disabled for the current member. */
	const isLockedRoute = (routeName) => locked.value && isChatOnlyBlockedRoute(routeName)

	return {
		locked,
		tooltip: CHAT_ONLY_TOOLTIP,
		allowedRoutes: CHAT_ONLY_ROUTES,
		isLockedRoute,
	}
}
